import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  FileText, Search, Download, Eye, Trash2, Clock, CheckCircle, 
  AlertCircle, Upload, Filter, RefreshCw, Book, Brain, Zap 
} from 'lucide-react';
import DocumentUpload from './DocumentUpload';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';

const DocumentManager = ({ knowledgeBaseId, knowledgeBaseName }) => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [documentStats, setDocumentStats] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    if (knowledgeBaseId) {
      fetchDocuments();
      fetchDocumentStats();
    }
  }, [knowledgeBaseId]);

  useEffect(() => {
    // Filter documents based on search query
    if (searchQuery.trim()) {
      const filtered = documents.filter(doc =>
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredDocuments(filtered);
    } else {
      setFilteredDocuments(documents);
    }
  }, [searchQuery, documents]);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(
        `/api/knowledge-bases/${knowledgeBaseId}/documents`
      );
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentStats = async () => {
    try {
      const response = await fetch(
        `/api/knowledge-bases/${knowledgeBaseId}/documents/stats`
      );
      
      if (response.ok) {
        const stats = await response.json();
        setDocumentStats(stats);
      }
    } catch (error) {
      console.error('Error fetching document stats:', error);
    }
  };

  const handleSemanticSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await fetch(
        `/api/knowledge-bases/${knowledgeBaseId}/documents/search`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            query: searchQuery,
            top_k: 10
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
        setShowSearchDialog(true);
      }
    } catch (error) {
      console.error('Error performing semantic search:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchDocumentVersions = async (documentId) => {
    try {
      const response = await fetch(
        `/api/documents/${documentId}/versions`
      );
      
      if (response.ok) {
        const data = await response.json();
        setVersions(data.versions || []);
        setShowVersionDialog(true);
      }
    } catch (error) {
      console.error('Error fetching document versions:', error);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document and all its versions?')) {
      return;
    }
    
    try {
      const response = await fetch(
        `/api/documents/${documentId}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        // Refresh documents list
        fetchDocuments();
        fetchDocumentStats();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'analyzed': return 'bg-purple-100 text-purple-800';
      case 'vectorized': return 'bg-indigo-100 text-indigo-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready': return <CheckCircle size={16} />;
      case 'processing': return <Clock size={16} className="animate-spin" />;
      case 'analyzed': return <Brain size={16} />;
      case 'vectorized': return <Zap size={16} />;
      case 'error': return <AlertCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      pdf: 'bg-red-100 text-red-600',
      docx: 'bg-blue-100 text-blue-600',
      txt: 'bg-gray-100 text-gray-600',
      md: 'bg-purple-100 text-purple-600'
    };
    return colors[type] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Document Manager</h2>
          <p className="text-gray-600">Knowledge Base: {knowledgeBaseName}</p>
        </div>
        <Button onClick={() => setShowUploadDialog(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Upload Document
        </Button>
      </div>

      {/* Statistics */}
      {documentStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Book className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{documentStats.total_documents}</p>
                  <p className="text-sm text-gray-600">Total Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold">{documentStats.total_chunks}</p>
                  <p className="text-sm text-gray-600">Vector Chunks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">
                    {Object.keys(documentStats.by_type || {}).length}
                  </p>
                  <p className="text-sm text-gray-600">File Types</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-orange-600" />
                <div>
                  <p className="text-2xl font-bold">Ready</p>
                  <p className="text-sm text-gray-600">RAG Status</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <Input
                placeholder="Search documents by title, description, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button 
              onClick={handleSemanticSearch}
              disabled={!searchQuery.trim() || searchLoading}
            >
              {searchLoading ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Brain className="mr-2 h-4 w-4" />
              )}
              AI Search
            </Button>
            <Button variant="outline" onClick={fetchDocuments}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documents ({filteredDocuments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">
                {documents.length === 0 ? 'No documents uploaded yet' : 'No documents match your search'}
              </p>
              {documents.length === 0 && (
                <Button 
                  className="mt-4" 
                  onClick={() => setShowUploadDialog(true)}
                >
                  Upload First Document
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredDocuments.map((doc) => {
                const currentVersion = doc.versions?.find(v => v.version_number === doc.current_version);
                const status = currentVersion?.status || 'unknown';
                
                return (
                  <div key={doc.document_id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{doc.title}</h3>
                          <Badge className={getDocumentTypeColor(doc.document_type)}>
                            {doc.document_type.toUpperCase()}
                          </Badge>
                          <Badge className={getStatusColor(status)}>
                            <span className="flex items-center gap-1">
                              {getStatusIcon(status)}
                              {status}
                            </span>
                          </Badge>
                        </div>
                        
                        {doc.description && (
                          <p className="text-gray-600 text-sm mb-2">{doc.description}</p>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>Version {doc.current_version}</span>
                          <span>Updated: {formatDate(doc.updated_at)}</span>
                          <span>Created: {formatDate(doc.created_at)}</span>
                        </div>
                        
                        {doc.tags && doc.tags.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {doc.tags.map((tag, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fetchDocumentVersions(doc.document_id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteDocument(doc.document_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-3xl">
          <DocumentUpload
            knowledgeBaseId={knowledgeBaseId}
            onUploadComplete={(result) => {
              fetchDocuments();
              fetchDocumentStats();
            }}
            onClose={() => setShowUploadDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Versions Dialog */}
      <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Document Versions</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {versions.map((version) => (
              <div key={version.version_id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">Version {version.version_number}</h4>
                  <Badge className={getStatusColor(version.status)}>
                    <span className="flex items-center gap-1">
                      {getStatusIcon(version.status)}
                      {version.status}
                    </span>
                  </Badge>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Uploaded: {formatDate(version.upload_timestamp)}</p>
                  {version.processed_timestamp && (
                    <p>Processed: {formatDate(version.processed_timestamp)}</p>
                  )}
                  <p>Size: {(version.file_size / 1024).toFixed(1)} KB</p>
                  {version.embedding_ids && (
                    <p>Embeddings: {version.embedding_ids.length} chunks</p>
                  )}
                </div>
                {version.analysis_result && (
                  <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                    <p><strong>Analysis:</strong> {version.analysis_result.summary}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Search Results Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Semantic Search Results</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Found {searchResults.length} relevant chunks for: "{searchQuery}"
            </p>
            {searchResults.map((result, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">Chunk {result.chunk_index + 1}</h4>
                  <Badge>Similarity: {(result.similarity * 100).toFixed(1)}%</Badge>
                </div>
                <p className="text-sm text-gray-700 mb-2">{result.content}</p>
                <div className="text-xs text-gray-500">
                  Document: {result.document_info?.[0]?.title}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentManager;